import React from "react";
import { Checkbox, FormControlLabel } from "@material-ui/core";
import CircularProgress from "@mui/material/CircularProgress";
import { Building, MapPin, Phone } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Link } from "react-router-dom";
import SingleSelectNew from "../../components/SelectField/SingleSelectNew";
import SingleSelectSearch from "../../components/SelectField/SelectSearch";
import { US_STATES } from "../../components/USStateConstant";

const RegisterStep2 = (props) => {
  const authInputClassName =
    "pl-40 bg-white text-[#0f172a] border-gray-300 placeholder:text-gray-500 focus-visible:ring-[#2980ba]";

  const {
    formState,
    handleInputChange,
    userRegister,
    industry,
    setErrorMessage,
    errorMessage,
    loading,
    onChangeCheckbox,
    state,
    error,
    peoples
  } = props;

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateAndGoNext();

    if (Object.keys(newErrors).length === 0) {
      userRegister(e);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    handleInputChange({ target: { name: "phoneNumber", value: value.slice(0, 10) } });
  };


  const validateAndGoNext = () => {
    const { companyName, phoneNumber, industry, peoples, companyCity, companyState } = formState;
    const newErrors = {};

    if (!companyName?.trim())
      newErrors.companyName = "Company name is required.";
    if (!phoneNumber?.trim()) newErrors.phoneNumber = "Phone Number is required.";
    else if (phoneNumber.length !== 10) newErrors.phoneNumber = "Phone Number must be 10 digits.";
    if (!industry) newErrors.industry = "Industry is required.";
    if (!peoples)
      newErrors.peoples = "Enter the number of peoples in your company.";
    if (!companyCity?.trim()) newErrors.companyCity = "City is required.";
    if (!companyState) newErrors.companyState = "State is required.";

    setErrorMessage(newErrors);
    return newErrors;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6 py-4 w-full px-4 sm:px-0">
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Business Details</h3>
        <p className="text-sm text-gray-500">
          This information helps us customize your experience.
        </p>

        <div className="space-y-2">
          <label htmlFor="phone" className="block text-sm font-medium">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              fullWidth={true}
              disableUnderline={true}
              onChange={handlePhoneChange}
              value={formState.phoneNumber ? formState.phoneNumber : ""}
              className={authInputClassName}
              name="phoneNumber"
              maxLength={15}
              id="phoneNumber"
              placeholder="Phone Number"
              tabIndex={7}
            />
          </div>
          {errorMessage.phoneNumber && (
            <div className="small error color-danger">
              {errorMessage.phoneNumber}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="company" className="block text-sm font-medium">
            Company Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Building className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              fullWidth={true}
              disableUnderline={true}
              onChange={handleInputChange}
              value={formState.companyName ? formState.companyName : ""}
              className={authInputClassName}
              name="companyName"
              id="companyName"
              maxLength={50}
              placeholder="Company Name"
              tabIndex={8}
            />
          </div>
          {errorMessage.companyName && (
            <div className="small error color-danger">
              {errorMessage.companyName}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <label htmlFor="companyCity" className="block text-sm font-medium">
              City <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                fullWidth={true}
                disableUnderline={true}
                onChange={handleInputChange}
                value={formState.companyCity ? formState.companyCity : ""}
                className={authInputClassName}
                name="companyCity"
                id="companyCity"
                maxLength={50}
                placeholder="City"
                tabIndex={9}
              />
            </div>
            {errorMessage.companyCity && (
              <div className="small error color-danger">
                {errorMessage.companyCity}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="companyState" className="block text-sm font-medium">
              State <span className="text-red-500">*</span>
            </label>
            <SingleSelectNew
              value={formState?.companyState ? formState?.companyState : ""}
              placeholder={"Select State"}
              name={"companyState"}
              className={"bg-white"}
              onChange={(val) => handleInputChange(val, "companyState")}
              options={US_STATES}
              tabIndex={10}
            />
            {errorMessage.companyState && (
              <div className="small error color-danger">
                {errorMessage.companyState}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="industry" className="block text-sm font-medium">
            Industry <span className="text-red-500">*</span>
          </label>
          <SingleSelectSearch
            value={formState?.industry ? formState?.industry : ""}
            placeholder={"Select Industry"}
            className={"bg-white"}
            name={"industry"}
            onChange={(val) => handleInputChange(val, "industry")}
            options={industry}
            tabIndex={11}
          />
          {errorMessage.industry && (
            <div className="small error color-danger">
              {errorMessage.industry}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="companySize" className="block text-sm font-medium">
            Company Size <span className="text-red-500">*</span>
          </label>
          <SingleSelectNew
            value={formState?.peoples ? formState?.peoples : ""}
            placeholder={"How many people in your company"}
            name={"peoples"}
            className={"bg-white"}
            onChange={(val) => handleInputChange(val, "peoples")}
            options={peoples}
            tabIndex={12}
          />
        </div>
        {errorMessage.peoples && (
          <div className="small error color-danger">{errorMessage.peoples}</div>
        )}
      </div>

      <div className="loginbottom">
        <FormControlLabel
          className="d-flex flex-center"
          control={
            <Checkbox
              value="yes"
              color="primary"
              checked={Boolean(state?.terms ? state?.terms : false)}
              onChange={onChangeCheckbox}
              tabIndex={13}
            />
          }
          label={
            <div className="text-center text-sm text-gray-500">
              I confirm that I have read and agree to the{" "}
              <Link
                to="/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-800 font-medium"
              >
                Terms and Conditions
              </Link>
              {" "}and{" "}
              <Link
                to="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-800 font-medium"
              >
                Privacy Policy
              </Link>
              .
            </div>
          }
        />
      </div>
      {error !== "" && error !== "Email is already registered" && <div className="small error color-danger">{error}</div>}
      <div className="pt-4">
        <Button
          type="submit"
          className="w-full"
          size="lg"
          tabIndex={14}
        >
          {loading ? (
            <CircularProgress size={28} style={{ color: "white" }} />
          ) : (
            "Complete Setup"
          )}
        </Button>
      </div>
    </form>
  );
};

export default RegisterStep2;
